import { Dayjs } from 'dayjs';
import { post } from '@core/api/dicomApi';

// Max studyInstanceUids accepted by a single AHDS $bulkUpdate request.
export const BULK_UPDATE_MAX_STUDIES = 50;

interface StudyData {
  patientId?: string;
  patientsGivenName?: string;
  patientsSurname?: string;
  patientsBirthDate?: Dayjs | null;
  patientsAge?: string;
  patientsGender?: string;
  studyDescription?: string;
  referringPhysiciansName?: string;
}

type BulkUpdateResponse = { id?: string; statusCode?: number };

// PatientAge (AS VR) is nnnD/W/M/Y; bare numbers are years, as the backend stores them.
const toDicomAge = (age: string) => (/^\d{1,3}$/.test(age.trim()) ? `${age.trim().padStart(3, '0')}Y` : age);

/**
 * Writes the given attributes into the DICOM of every study through $bulkUpdate.
 * Resolves to false when any request was rejected (the api layer swallows HTTP
 * errors into `{ statusCode }`), true otherwise or when there was nothing to write.
 */
const updateStudy = async (
  studyInstanceUids: string | string[],
  payload: StudyData,
): Promise<boolean> => {
  const uids = (Array.isArray(studyInstanceUids) ? studyInstanceUids : [studyInstanceUids]).filter(
    Boolean,
  );

  const formatedDate = payload?.patientsBirthDate?.isValid()
    ? payload.patientsBirthDate.format('YYYYMMDD')
    : undefined;

  let formattedDicomData = {};

  if (payload.patientId) {
    formattedDicomData = {
      ...formattedDicomData,
      '00100020': {
        vr: 'LO',
        Value: [payload.patientId],
      },
    };
  }

  if (payload.patientsGivenName || payload.patientsSurname) {
    formattedDicomData = {
      ...formattedDicomData,
      '00100010': {
        vr: 'PN',
        Value: [
          {
            Alphabetic: `${payload.patientsGivenName} ${payload.patientsSurname}`,
          },
        ],
      },
    };
  }

  if (payload.patientsAge) {
    formattedDicomData = {
      ...formattedDicomData,
      '00101010': {
        vr: 'AS',
        Value: [toDicomAge(payload.patientsAge)],
      },
    };
  }

  if (payload.patientsGender) {
    formattedDicomData = {
      ...formattedDicomData,
      '00100040': {
        vr: 'CS',
        Value: [payload.patientsGender],
      },
    };
  }

  if (payload.studyDescription) {
    formattedDicomData = {
      ...formattedDicomData,
      '00081030': {
        vr: 'LO',
        Value: [payload.studyDescription],
      },
    };
  }

  if (payload.referringPhysiciansName) {
    formattedDicomData = {
      ...formattedDicomData,
      '00080090': {
        vr: 'PN',
        Value: [
          {
            Alphabetic: payload.referringPhysiciansName,
          },
        ],
      },
    };
  }

  if (formatedDate) {
    formattedDicomData = {
      ...formattedDicomData,
      '00100030': {
        vr: 'DA',
        Value: [formatedDate],
      },
    };
  }

  if (!uids.length || !Object.keys(formattedDicomData).length) {
    return true;
  }

  const chunks: string[][] = [];
  for (let i = 0; i < uids.length; i += BULK_UPDATE_MAX_STUDIES) {
    chunks.push(uids.slice(i, i + BULK_UPDATE_MAX_STUDIES));
  }

  const responses = await Promise.all(
    chunks.map((chunk) =>
      post<BulkUpdateResponse, unknown>(`/studies/$bulkUpdate`, {
        studyInstanceUids: chunk,
        changeDataset: formattedDicomData,
      }),
    ),
  );

  return responses.every((response) => !response?.statusCode);
};
export default updateStudy;
